import React, { useState, useContext } from 'react';
import numeral from 'numeral';
import i18next from 'i18next';
import NumberFormat from 'react-number-format';
import { Field } from 'react-form';
import {
  InputGroup,
  InputGroupAddon,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import classNames from 'classnames';
import { isEmpty, get } from 'lodash';
import { connect } from 'react-redux';
import { getConfig } from 'config';
import __ from 'utils/i18n';
import { PermissionsContext } from "contexts/permissionsContext";

const countAmountDigits = (str) => {
  if (!str || typeof str !== 'string') return 0;
  return (str.replace(/\D/g, '') || '').length;
};

const AmountInput = (props) => {
  const { value, className, type, name, userCurrencies, maxIntegerDigits: propMaxIntegerDigits, maxAmountDigits: propMaxAmountDigits, allowEmptyAtStart = false } = props;
  const [isDropdownOpen, setIsDropwdownOpen] = useState(false);
  const { featureFlags } = useContext(PermissionsContext);
  const showDecimals = getConfig('customConfig.currencyDecimals');
  const maxIntegerDigits = propMaxIntegerDigits !== undefined && propMaxIntegerDigits !== null 
    ? propMaxIntegerDigits 
    : getConfig('customConfig.maxAmountLimit');

  const formatCurrencies = userCurrencies
    ? userCurrencies.map((item) => ({
      value: item.code,
      label: item.symbol,
    }))
    : null;

  const currencies = formatCurrencies || getConfig('customConfig.currencies', []);
  const isMultiCurrenciesEnabled = featureFlags?.includes('multicurrency');

  const showCurrencies = currencies && currencies.length > 1 && isMultiCurrenciesEnabled;

  let delimiters = numeral.locales[i18next.language].delimiters;

  const localSettingsNumeral = Object.entries(numeral.locales).filter(
    (x) => x[0].indexOf(`${i18next.language}-`) !== -1
  );

  if (!isEmpty(localSettingsNumeral)) {
    delimiters = localSettingsNumeral[0][1].delimiters;
  }

  const { thousands, decimal, precision } = delimiters;

  let formattedValuePlaceholder = () => {
    if (allowEmptyAtStart && (value === null || value === undefined)) {
      return '';
    }
    return showDecimals ?
      __.number(value && value.amount, 'rounded') || `0${decimal}00` :
      __.number(value && value.amount, 'rounded');
  }


  const maxAmountDigits = propMaxAmountDigits;

  const isAllowed = (values) => {
    const { floatValue, value: valueStr } = values;

    // Si no hay valor, permitir (para poder borrar)
    if (floatValue === null || floatValue === undefined) {
      return true;
    }

    // Validar que sea un número válido y finito
    if (isNaN(floatValue) || !isFinite(floatValue)) {
      return false;
    }

    // Validar que no exceda el máximo seguro de JavaScript
    // Number.MAX_SAFE_INTEGER = 9007199254740991 (2^53 - 1)
    if (floatValue > Number.MAX_SAFE_INTEGER) {
      return false;
    }

    // Validar que no sea negativo
    if (floatValue < 0) {
      return false;
    }

    // PR-135: Max 9 digits total (integer + decimal) - prevents values like 0.000000000000000001234567
    if (maxAmountDigits !== undefined && maxAmountDigits !== null && valueStr) {
      const digitCount = countAmountDigits(valueStr);
      if (digitCount > maxAmountDigits) {
        return false;
      }
    }

    // Validar cantidad máxima de dígitos en la parte entera
    if (maxIntegerDigits !== undefined && maxIntegerDigits !== null) {
      // Extraer solo la parte entera (sin decimales)
      const integerPart = Math.floor(Math.abs(floatValue));
      // Contar los dígitos de la parte entera
      const integerDigits = integerPart.toString().length;
      // Validar que no exceda el límite de dígitos
      return integerDigits <= maxIntegerDigits;
    }

    return true;
  };

  const onChangeAmount = (amount) => {
    props.onChange({
      ...props.value,
      amount,
    });
  };

  const onChangeCurrency = (currency) => {
    props.onChange({
      ...props.value,
      currency: currency.value,
    });
  };

  return (
    <Field field={name} pure={false}>
      {({ setValue, value, setTouched }) => {
        return (
          <InputGroup>
            <NumberFormat
              className={classNames('form-control input-amount', className)}
              value={allowEmptyAtStart ? (value?.amount || '') : value.amount}
              thousandSeparator={thousands}
              decimalSeparator={decimal}
              decimalScale={precision}
              allowNegative={false}
              placeholder={formattedValuePlaceholder}
              isAllowed={isAllowed}
              onChange={(e) => {
                setTouched();
                onChangeAmount(e.target.value);
              }}
            />
            {type === 'amount' && (
              <>
                {showCurrencies ? (
                  <InputGroupAddon addonType="prepend">
                    {isMultiCurrenciesEnabled ? (
                      <ButtonDropdown
                        isOpen={isDropdownOpen}
                        toggle={() =>
                          setIsDropwdownOpen((prevState) => !prevState)
                        }
                      >
                        <DropdownToggle color="secondary" caret>
                          {__.currency(
                            false,
                            value && value.currency,
                            'decimal',
                            ' '
                          )}
                        </DropdownToggle>
                        <DropdownMenu right>
                          {currencies.map((currency, index) => {
                            return (
                              <DropdownItem
                                key={index}
                                onClick={() => {
                                  onChangeCurrency(currency);
                                  setTouched();
                                }}
                              >
                                {__.currency(
                                  false,
                                  currency.value,
                                  'decimal',
                                  ' '
                                )}
                              </DropdownItem>
                            );
                          })}
                        </DropdownMenu>
                      </ButtonDropdown>
                    ) : (
                      <span className="input-group-text">
                        {__.currency(
                          false,
                          value && value.currency,
                          'decimal',
                          ' '
                        )}
                      </span>
                    )}
                  </InputGroupAddon>
                ) : (
                  <div className="center-prefix-number input-group-prepend  center-prefix-currency">
                    <span>
                      {__.currency(false, currencies[0].value, 'decimal', ' ')}
                    </span>
                  </div>
                )}
              </>
            )}
            {type === 'percentage' && (
              <div className="center-prefix-number input-group-prepend">
                <span style={{ fontSize: '18px' }}>%</span>
              </div>
            )}
          </InputGroup>
        );
      }}
    </Field>
  );
};

const mapStateToProps = (state) => ({
  userCurrencies: get(state, 'login.user.currencies', null),
});

export default connect(mapStateToProps)(AmountInput);
